import { Component, inject, ViewChild, ChangeDetectionStrategy } from '@angular/core';
import { MatTableDataSource } from '@angular/material/table';
import { Router } from '@angular/router';
import { TableComponent } from 'src/app/components/table/table.component';
import { AppConstants } from 'src/app/constants/app-constants';
import { Invitation } from 'src/app/model/Invitation';
import { ColumnDefinition, ServiceInterface, TableAction, TableConstruct } from 'src/app/model/Table';
import { InvitationService } from 'src/app/services/invitation/invitation.service';
import { RunningActions } from 'src/app/services/running-actions/running-actions.service';
import { ToastrService } from 'ngx-toastr';
import { HttpErrorResponse } from '@angular/common/http';

@Component({
    selector: 'app-invite-page',
    templateUrl: './invite-page.component.html',
    styleUrls: ['./invite-page.component.scss'],
    changeDetection: ChangeDetectionStrategy.Eager,
    standalone: false
})
export class InvitePageComponent {
  /** Injected dependencies */
  private readonly _invitationService: InvitationService = inject(InvitationService);
  private readonly _router: Router = inject(Router);
  private readonly _toastrService: ToastrService = inject(ToastrService);
  private _runningActions = inject(RunningActions);

  /** Columns to display in the table */
  public displayedColumns: string[];

  /** Reference to the application table element */
  @ViewChild("invitationTable") table!: TableComponent<Invitation>;

  /** Table construct to pass to the table component */
  public construct: TableConstruct<Invitation>;

  /** Column definitions with actions */
  public columnDefinitions: ColumnDefinition<Invitation>[] = [
    { field: 'name', header: 'Name' },
    { field: 'businessName', header: 'Business Name' },
    { field: 'email', header: 'Email' },
    {
      field: 'lastSent',
      header: "Sent Date",
      format: "date",
      toStringTransform: (x) => new Date(x).toDateString()
    },
    {
      field: 'actions',
      header: "Actions",
      actions: [
        {
          display: "Resend Invite",
          disabledMsg: 'Sent today',
          loadingMsg: 'Resending...',
          // Use an arrow function so that `this` refers to the component instance
          click: (event: any, invitation: Invitation): void => {
            this.resendInvitation(invitation, this.columnDefinitions![4].actions![0]);
          },
          disable: (invitation: Invitation): boolean => {
            return false;
            /* Removed as of TBYND 834 - resend allowed any time
            const lastSent = DateTime.fromFormat(invitation.lastSent!, AppConstants.DATE_FORMAT);
            if (lastSent.isValid === false) {
              throw new Error(`Invalid last sent date format: ${invitation.lastSent}`);
            }
            const today = DateTime.now();
            // If the last sent date is not today, allow resend
            return lastSent.hasSame(today, 'day');
            */
          },
          constraint: () => true
        },
      ]
    },
  ];

  /**
   * Constructor initializes the table construct and displayed columns
   */
  constructor() {
    // Set displayed columns from the column definitions
    this.displayedColumns = this.columnDefinitions.map(x => x.field);

    // Create the table construct object
    const serviceInterface: ServiceInterface = {
      timeSinceLastRefresh$: this._invitationService.timeSinceLastRefresh$,
      changes$: this._invitationService.invitations$,
      getEntities: () => this._invitationService.get(),
    };

    this.construct = {
      source: new MatTableDataSource<Invitation>(),
      columns: this.columnDefinitions,
      displayedColumns: this.displayedColumns,
      service: serviceInterface,
      paginationRange: [5, 10, 25, 100],
      fetchConstraints: this._createFetchConstraints(),
      settings: { sort: { enabled: true } },
    };
  }

  /**
   * The resendInvitation method calls the InvitationService resend method
   * and updates the invitation’s lastSent field on success.
   */
  public async resendInvitation(invitation: Invitation, action: TableAction<Invitation>): Promise<void> {
    try {
      this._runningActions.set(action, invitation);

      const msg = await this._invitationService.resendInvitation(invitation.copyOf());
      this._toastrService.success(msg);
    } catch (error: any) {
      console.error('Error resending invitation', error);
      this._toastrService.error(error.error?.message ?? 'Error resending invitation');
    } finally {
      this._runningActions.unset(action, invitation);
    }
  }

  /**
   * Constraints on when to fetch invitation data.
   * Return true to block the source update.
   */
  private _createFetchConstraints(): () => boolean {
    return (): boolean => {
      return !this._router.url.endsWith(AppConstants.MENU_APPLICATIONS_INVITES);
    }
  }

  /**
   * Apply a filter to the table (if your table component uses this)
   */
  public applyFilter(event: Event): void {
    const filterValue = (event.target as HTMLInputElement).value;
    this.table.construct.source.filter = filterValue.trim().toLowerCase();
  }

  /**
   * Export the table data to Excel
   */
  public exportInvitationsToExcel(): void {
    this.table.exportInvitationsToXlsx();
  }
}
